// Shared sales-meetings primitives — used by MeetingsThread (deal sidebar),
// MeetingModal (create/edit), and SalesCalendar (CP2). Extracted so the modal
// and the status/completion logic live in exactly one place. sales_meetings IS
// in the generated types; we keep the cast-the-client convention that the
// sibling sales-* surfaces use.

import { format } from 'date-fns'
import { supabase } from '@/lib/supabase'

export type MeetingEntityType = 'deal' | 'contact' | 'company'
export type MeetingStatus = 'scheduled' | 'completed' | 'canceled' | 'no_show'

export interface MeetingRow {
  id: string
  title: string
  entity_type: string | null
  entity_id: string | null
  start_time: string
  end_time: string
  location: string | null
  notes: string | null
  attendee_emails: string[]
  status: string
  owner_id: string | null
  remind_at: string | null
  reminded_at: string | null
  meeting_room_token: string | null
  created_at: string
}

export const STATUS_META: Record<string, { label: string; color: string }> = {
  scheduled: { label: 'Scheduled', color: 'var(--info)' },
  completed: { label: 'Completed', color: 'var(--positive)' },
  canceled:  { label: 'Canceled',  color: 'var(--ink-tertiary)' },
  no_show:   { label: 'No-show',   color: 'var(--danger)' },
}

// Stored timestamptz (ISO) -> value for <input type="datetime-local"> (local tz).
export const toLocalInput = (iso: string) => format(new Date(iso), "yyyy-MM-dd'T'HH:mm")
export const isUrl = (s: string | null) => !!s && /^https?:\/\//i.test(s.trim())

// Local date key ('yyyy-MM-dd') for grouping meetings into calendar day cells,
// computed in the browser's local tz (timestamptz round-trips correctly).
export const localDayKey = (iso: string) => format(new Date(iso), 'yyyy-MM-dd')

// Set a meeting's status. Completing a DEAL-linked meeting also writes a
// deal_activities meeting_logged row (matching DealDetail's shape) so the deal
// timeline reflects it. Keyed off the MEETING's own linkage, so it's correct
// whether called from the deal sidebar or the standalone calendar.
export async function setMeetingStatus(
  meeting: MeetingRow,
  status: MeetingStatus,
  ctx: { workspaceId: string },
): Promise<void> {
  const { error } = await (supabase as any).from('sales_meetings').update({ status }).eq('id', meeting.id)
  if (error) throw error
  if (status === 'completed' && meeting.entity_type === 'deal' && meeting.entity_id) {
    const { error: actErr } = await supabase.from('deal_activities').insert({
      deal_id: meeting.entity_id,
      workspace_id: ctx.workspaceId,
      type: 'meeting_logged',
      payload: { when: meeting.start_time, title: meeting.title, meeting_id: meeting.id },
    })
    if (actErr) throw actErr
  }
}

export async function deleteMeeting(id: string): Promise<void> {
  const { error } = await (supabase as any).from('sales_meetings').delete().eq('id', id)
  if (error) throw error
}
